import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdminSettings } from '../contexts/AdminSettingsContext';
import api from '../services/api';
import axios from 'axios';
import {
  User, Mail, Phone, MapPin, Calendar, Key, Save,
  Eye, EyeOff, Settings, Target, ToggleLeft, ToggleRight,
  Camera, Trash2, Droplets, Heart, BookOpen, CheckCircle, XCircle,
  Download, CreditCard
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import ConfirmModal from '../components/ConfirmModal';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { getTodayLocalISO } from '../utils/dateUtils';

const ROLE_CONFIG = {
  membro:       { label: 'Membro',         bg: '#64748b' },
  secretario:   { label: 'Secretário',     bg: '#3b82f6' },
  tesoureiro:   { label: 'Tesoureiro',     bg: '#3b82f6' },
  pastor:       { label: 'Pastor',         bg: '#f59e0b' },
  administracao:{ label: 'Administração',  bg: '#ef4444' },
};

const TABS = ['Pessoal', 'Espiritual', 'Carteirinha', 'Segurança'];

const toInputDate = (val) => {
  if (!val) return '';
  try {
    if (typeof val === 'string') {
      const m = val.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
      const d = new Date(val);
      return isNaN(d) ? '' : format(d, 'yyyy-MM-dd');
    }
    if (val instanceof Date) return format(val, 'yyyy-MM-dd');
  } catch {}
  return '';
};

// Formata data sem deslocamento de timezone (evita UTC → local = dia anterior)
const fmtDate = (val) => {
  if (!val) return '';
  try {
    const s = typeof val === 'string' ? val : val instanceof Date ? val.toISOString() : String(val);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const local = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return format(local, 'dd/MM/yyyy', { locale: ptBR });
    }
  } catch {}
  return '';
};

// ── Campo de input com estilo unificado ─────────────────────────────────────
const Field = ({ label, icon: Icon, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
      {Icon && <Icon size={14} color="#94a3b8" />}{label}
    </label>
    {children}
  </div>
);

const inputStyle = (disabled) => ({
  width: '100%', padding: '10px 12px',
  border: '1px solid #e2e8f0', borderRadius: '8px',
  fontSize: '14px', color: disabled ? '#94a3b8' : '#1e293b',
  background: disabled ? '#f8fafc' : '#ffffff',
  outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
});

// ── Indicador espiritual ────────────────────────────────────────────────────
const SpiritualBadge = ({ icon: Icon, label, done, color }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '12px 16px', borderRadius: '10px',
    background: done ? `${color}15` : '#f8fafc',
    border: `1px solid ${done ? color : '#e2e8f0'}`,
  }}>
    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: done ? color : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={18} color="#fff" />
    </div>
    <span style={{ fontSize: '14px', fontWeight: '500', color: done ? color : '#94a3b8', flex: 1 }}>{label}</span>
    {done
      ? <CheckCircle size={18} color={color} />
      : <XCircle size={18} color="#d1d5db" />}
  </div>
);

// ── Componente principal ────────────────────────────────────────────────────
const Profile = () => {
  const { user, updateUser } = useAuth();
  const { settings, toggleMissionsVisibility, canManageSettings } = useAdminSettings();

  const [activeTab, setActiveTab] = useState('Pessoal');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Avatar
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [showRemoveAvatar, setShowRemoveAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  const qrCanvasRef = useRef(null);

  // Formulários
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '',
    address: { street: '', city: '', state: '', zipCode: '' },
    birthDate: '',
  });
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);

  // Espiritual
  const [acceptedJesus, setAcceptedJesus] = useState(null);
  const [baptizedWaters, setBaptizedWaters] = useState(null);
  const [encontroComDeus, setEncontroComDeus] = useState(null);
  const [extraInfo, setExtraInfo] = useState({ conversionDate: '', baptismDate: '', encontroDate: '', encounters: '' });
  const [extraEditing, setExtraEditing] = useState(false);
  const [extraSaving, setExtraSaving] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Sincronizar dados do user
  const syncFromUser = useCallback((u) => {
    if (!u) return;
    setFormData({
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      address: {
        street: u.address?.street || '',
        city: u.address?.city || '',
        state: u.address?.state || '',
        zipCode: u.address?.zipCode || '',
      },
      birthDate: u.birthDate ? toInputDate(u.birthDate) : '',
    });
    setAcceptedJesus(u.acceptedJesus === true ? true : u.acceptedJesus === false ? false : null);
    setBaptizedWaters(u.baptized === true ? true : u.baptized === false ? false : null);
    setEncontroComDeus(u.encontroComDeus === true ? true : u.encontroComDeus === false ? false : null);
    setExtraInfo({
      conversionDate: u.conversionDate ? toInputDate(u.conversionDate) : '',
      baptismDate: u.baptismDate ? toInputDate(u.baptismDate) : '',
      encontroDate: u.encontroComDeusDate ? toInputDate(u.encontroComDeusDate) : '',
      encounters: u.encountersNotes || '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { syncFromUser(user); }, [user, syncFromUser]);

  useEffect(() => {
    const load = async () => {
      try {
        if (user && user.phone && user.birthDate) { setInitialLoading(false); return; }
        const res = await api.get('/auth/verify');
        if (res.data?.user) { updateUser(res.data.user); syncFromUser(res.data.user); }
      } catch {}
      finally { setInitialLoading(false); }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CEP
  const handleZipChange = async (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    const formatted = digits.length > 5 ? `${digits.slice(0,5)}-${digits.slice(5)}` : digits;
    setFormData(p => ({ ...p, address: { ...p.address, zipCode: formatted } }));
    if (digits.length === 8) {
      try {
        setCepLoading(true);
        const res = await axios.get(`https://viacep.com.br/ws/${digits}/json/`);
        if (!res.data.erro) {
          setFormData(p => ({ ...p, address: { ...p.address, street: res.data.logradouro || p.address.street, city: res.data.localidade || p.address.city, state: res.data.uf || p.address.state } }));
        } else toast.warn('CEP não encontrado');
      } catch { toast.error('Erro ao buscar CEP'); }
      finally { setCepLoading(false); }
    }
  };

  // Avatar: comprime e redimensiona antes de exibir/enviar
  const compressImage = (file) => new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX = 600;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const ratio = Math.min(MAX / width, MAX / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        resolve(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.82);
    };
    img.src = url;
  });

  // Avatar: selecionar arquivo
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file);
    setAvatarFile(compressed);
    setAvatarPreview(URL.createObjectURL(compressed));
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;
    try {
      setAvatarLoading(true);
      const fd = new FormData();
      fd.append('avatar', avatarFile);
      const res = await api.post('/users/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateUser(res.data.user);
      setAvatarFile(null);
      setAvatarPreview(null);
      toast.success('Foto atualizada!');
    } catch { toast.error('Erro ao salvar foto'); }
    finally { setAvatarLoading(false); }
  };

  const handleAvatarRemove = async () => {
    try {
      setAvatarLoading(true);
      const res = await api.delete('/users/avatar');
      updateUser(res.data.user);
      setAvatarPreview(null);
      setAvatarFile(null);
      setShowRemoveAvatar(false);
      toast.success('Foto removida');
    } catch { toast.error('Erro ao remover foto'); }
    finally { setAvatarLoading(false); }
  };

  const cancelAvatarPreview = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  // Perfil pessoal
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.put('/users/profile', formData);
      if (res.data.user) { updateUser(res.data.user); setIsEditing(false); toast.success('Perfil atualizado!'); }
    } catch (err) { toast.error(err.response?.data?.message || 'Erro ao atualizar perfil'); }
    finally { setLoading(false); }
  };

  // Senha
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) { toast.error('As senhas não coincidem'); return; }
    if (passwordData.newPassword.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    setLoading(true);
    try {
      await api.put('/users/password', { currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Senha alterada!');
    } catch (err) {
      toast.error(err.response?.status === 400 ? 'Senha atual incorreta' : 'Erro ao alterar senha');
    }
    finally { setLoading(false); }
  };

  // Espiritual
  const handleExtraSubmit = async (e) => {
    e.preventDefault();
    const today = new Date(getTodayLocalISO());
    const conv = extraInfo.conversionDate ? new Date(extraInfo.conversionDate) : null;
    const bapt = extraInfo.baptismDate ? new Date(extraInfo.baptismDate) : null;
    if (acceptedJesus === true && !conv) { toast.error('Informe a data da conversão'); return; }
    if (acceptedJesus === true && conv > today) { toast.error('Data da conversão não pode ser futura'); return; }
    if (baptizedWaters === true && !bapt) { toast.error('Informe a data do batismo'); return; }
    if (baptizedWaters === true && bapt > today) { toast.error('Data do batismo não pode ser futura'); return; }
    const encDate = extraInfo.encontroDate ? new Date(extraInfo.encontroDate) : null;
    if (encontroComDeus === true && !encDate) { toast.error('Informe a data do Encontro com Deus'); return; }
    if (encontroComDeus === true && encDate > today) { toast.error('Data do Encontro não pode ser futura'); return; }
    try {
      setExtraSaving(true);
      const res = await api.put('/users/extra', {
        acceptedJesus: acceptedJesus === true,
        conversionDate: extraInfo.conversionDate || undefined,
        baptizedWaters: baptizedWaters === true,
        baptismDate: extraInfo.baptismDate || undefined,
        encontroComDeus: encontroComDeus === true,
        encontroComDeusDate: extraInfo.encontroDate || undefined,
        encounters: extraInfo.encounters || '',
      });
      if (res.data?.user) {
        updateUser(res.data.user);
        syncFromUser(res.data.user);
        toast.success('Informações espirituais salvas');
      }
      setExtraEditing(false);
    } catch (err) { toast.error(err?.response?.data?.message || 'Erro ao salvar'); }
    finally { setExtraSaving(false); }
  };

  const roleConfig = ROLE_CONFIG[user?.role] || { label: user?.role, bg: '#64748b' };

  const handleDownloadQR = () => {
    const canvas = qrCanvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `carteirinha-${(user?.name || 'membro').replace(/\s+/g, '-').toLowerCase()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };
  const API_STATIC = (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && window.location.port === '3000') ? 'http://localhost:3001' : '';
  const avatarSrc = avatarPreview || (user?.avatar ? `${API_STATIC}${user.avatar}` : null);
  const initials = (user?.name || '').split(' ').filter(Boolean).slice(0, 2).map(n => n[0].toUpperCase()).join('');

  if (!user || initialLoading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#64748b', fontSize: '14px' }}>Carregando perfil...</div>;
  }

  return (
    <>
    <div style={{ maxWidth: '860px', margin: '0 auto' }}>

      {/* ── HEADER: banner + avatar ───────────────────────────────────────── */}
      <div style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
        {/* Banner */}
        <div style={{ height: '120px', background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 60%, #60a5fa 100%)', position: 'relative' }}>
          <div style={{ position: 'absolute', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', top: '-60px', right: '-40px' }} />
          <div style={{ position: 'absolute', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', bottom: '-20px', left: '40%' }} />
        </div>

        {/* Info do usuário */}
        <div style={{ background: '#ffffff', padding: '0 24px 24px', position: 'relative' }}>
          {/* Avatar */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ position: 'relative', marginTop: '-44px' }}>
              <div
                style={{ width: '88px', height: '88px', borderRadius: '50%', border: '4px solid #ffffff', overflow: 'hidden', background: avatarSrc ? 'transparent' : '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                onClick={() => avatarInputRef.current?.click()}
                role="button"
                tabIndex={0}
                aria-label="Alterar foto de perfil"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    avatarInputRef.current?.click();
                  }
                }}
                title="Clique para alterar foto"
              >
                {avatarSrc
                  ? <img src={avatarSrc} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: '28px', fontWeight: '700', color: '#fff' }}>{initials}</span>
                }
                {/* Overlay câmera */}
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s', borderRadius: '50%' }}
                  onMouseOver={e => e.currentTarget.style.opacity = '1'}
                  onMouseOut={e => e.currentTarget.style.opacity = '0'}
                >
                  <Camera size={22} color="#fff" />
                </div>
              </div>
              <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
            </div>

            {/* Botões de avatar */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {avatarFile && (
                <>
                  <button onClick={cancelAvatarPreview} style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '13px', cursor: 'pointer', color: '#64748b' }}>Cancelar</button>
                  <button onClick={handleAvatarUpload} disabled={avatarLoading} style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: avatarLoading ? 'not-allowed' : 'pointer' }}>
                    {avatarLoading ? 'Salvando...' : 'Salvar foto'}
                  </button>
                </>
              )}
              {!avatarFile && user?.avatar && (
                <button onClick={() => setShowRemoveAvatar(true)} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #fecdd3', background: '#fff1f2', color: '#ef4444', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Trash2 size={13} /> Remover foto
                </button>
              )}
            </div>
          </div>

          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px' }}>{user.name}</h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 10px' }}>{user.email}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-block', fontSize: '12px', fontWeight: '600', color: '#fff', background: roleConfig.bg, borderRadius: '20px', padding: '3px 12px' }}>
              {roleConfig.label}
            </span>
            {user.emailVerified && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '20px', padding: '3px 10px' }}>
                <CheckCircle size={12} /> Email verificado
              </span>
            )}
            {user.createdAt && (
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                Membro desde {format(new Date(user.createdAt), 'MMM yyyy', { locale: ptBR })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── ABAS ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', borderRadius: '12px', padding: '4px', marginBottom: '20px' }}>
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            flex: 1, padding: '9px', borderRadius: '9px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
            background: activeTab === tab ? '#ffffff' : 'transparent',
            color: activeTab === tab ? '#1e293b' : '#64748b',
            boxShadow: activeTab === tab ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.2s',
          }}>
            {tab}
          </button>
        ))}
      </div>

      {/* ── ABA PESSOAL ──────────────────────────────────────────────────── */}
      {activeTab === 'Pessoal' && (
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Informações Pessoais</h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#94a3b8' }}>Nome e email não podem ser alterados</p>
            </div>
            {!isEditing && (
              <button onClick={() => setIsEditing(true)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#fff', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                Editar
              </button>
            )}
          </div>
          <form onSubmit={handleProfileUpdate}>
            <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <Field label="Nome Completo" icon={User}>
                <input style={inputStyle(true)} value={formData.name} disabled />
              </Field>
              <Field label="Email" icon={Mail}>
                <input style={inputStyle(true)} value={formData.email} disabled />
              </Field>
              <Field label="Telefone" icon={Phone}>
                <input
                  style={inputStyle(!isEditing)}
                  value={formData.phone}
                  onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                  disabled={!isEditing}
                  placeholder="(00) 00000-0000"
                  onFocus={e => { if (isEditing) { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)'; } }}
                  onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                />
              </Field>
              <Field label="Data de Nascimento" icon={Calendar}>
                <input
                  type="date"
                  style={inputStyle(!isEditing)}
                  value={formData.birthDate}
                  onChange={e => setFormData(p => ({ ...p, birthDate: e.target.value }))}
                  disabled={!isEditing}
                  onFocus={e => { if (isEditing) { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)'; } }}
                  onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                />
              </Field>

              {/* Endereço — linha inteira */}
              <div style={{ gridColumn: '1 / -1' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <MapPin size={14} color="#94a3b8" /> Endereço
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                  {[
                    { key: 'zipCode', placeholder: cepLoading ? 'Buscando...' : 'CEP', handler: handleZipChange },
                    { key: 'street', placeholder: 'Rua e número', handler: e => setFormData(p => ({ ...p, address: { ...p.address, street: e.target.value } })) },
                    { key: 'city', placeholder: 'Cidade', handler: e => setFormData(p => ({ ...p, address: { ...p.address, city: e.target.value } })) },
                    { key: 'state', placeholder: 'Estado', handler: e => setFormData(p => ({ ...p, address: { ...p.address, state: e.target.value } })) },
                  ].map(({ key, placeholder, handler }) => (
                    <input key={key} style={inputStyle(!isEditing)} value={formData.address[key]} onChange={handler} disabled={!isEditing} placeholder={placeholder}
                      onFocus={e => { if (isEditing) { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)'; } }}
                      onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {isEditing && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button type="button" onClick={() => { syncFromUser(user); setIsEditing(false); }} disabled={loading} style={{ padding: '9px 18px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#fff', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button type="submit" disabled={loading} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Save size={14} /> {loading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            )}
          </form>
        </div>
      )}

      {/* ── ABA ESPIRITUAL ───────────────────────────────────────────────── */}
      {activeTab === 'Espiritual' && (
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Jornada Espiritual</h3>
            {!extraEditing && (
              <button onClick={() => setExtraEditing(true)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#fff', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                Editar
              </button>
            )}
          </div>

          {!extraEditing ? (
            /* Modo visualização — badges */
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <SpiritualBadge icon={CheckCircle} label="Aceitou Jesus" done={!!user?.acceptedJesus} color="#10b981" />
              {user?.conversionDate && (
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: '-2px 0 0 52px' }}>
                  Data: {fmtDate(user.conversionDate)}
                </p>
              )}
              <SpiritualBadge icon={Droplets} label="Batizado em Águas" done={!!user?.baptized} color="#3b82f6" />
              {user?.baptismDate && (
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: '-2px 0 0 52px' }}>
                  Data: {fmtDate(user.baptismDate)}
                </p>
              )}
              <SpiritualBadge icon={Heart} label="Encontro com Deus" done={!!user?.encontroComDeus} color="#ef4444" />
              {user?.encontroComDeusDate && (
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: '-2px 0 0 52px' }}>
                  Data: {fmtDate(user.encontroComDeusDate)}
                </p>
              )}
              <SpiritualBadge icon={BookOpen} label="DNA Apascentar" done={!!user?.dnaApascentar} color="#f97316" />
              {user?.encountersNotes && (
                <div style={{ marginTop: '8px', padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '13px', color: '#64748b' }}>
                  <strong style={{ color: '#374151' }}>Observações: </strong>{user.encountersNotes}
                </div>
              )}
            </div>
          ) : (
            /* Modo edição */
            <form onSubmit={handleExtraSubmit}>
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

                {/* Aceitou Jesus */}
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: '0 0 10px' }}>Você já aceitou Jesus?</p>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {[true, false].map(val => (
                      <label key={String(val)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', border: `1.5px solid ${acceptedJesus === val ? '#3b82f6' : '#e2e8f0'}`, background: acceptedJesus === val ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                        <input type="radio" name="acceptedJesus" checked={acceptedJesus === val} onChange={() => { setAcceptedJesus(val); if (!val) setExtraInfo(p => ({ ...p, conversionDate: '' })); }} style={{ accentColor: '#3b82f6' }} />
                        {val ? 'Sim' : 'Não'}
                      </label>
                    ))}
                  </div>
                  {acceptedJesus === true && (
                    <div style={{ marginTop: '10px' }}>
                      <Field label="Data da Conversão" icon={Calendar}>
                        <input type="date" style={inputStyle(false)} value={extraInfo.conversionDate} max={getTodayLocalISO()} onChange={e => setExtraInfo(p => ({ ...p, conversionDate: e.target.value }))} required />
                      </Field>
                    </div>
                  )}
                </div>

                {/* Batismo */}
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: '0 0 10px' }}>Já se batizou em Águas?</p>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {[true, false].map(val => (
                      <label key={String(val)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', border: `1.5px solid ${baptizedWaters === val ? '#3b82f6' : '#e2e8f0'}`, background: baptizedWaters === val ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                        <input type="radio" name="baptizedWaters" checked={baptizedWaters === val} onChange={() => { setBaptizedWaters(val); if (!val) setExtraInfo(p => ({ ...p, baptismDate: '' })); }} style={{ accentColor: '#3b82f6' }} />
                        {val ? 'Sim' : 'Não'}
                      </label>
                    ))}
                  </div>
                  {baptizedWaters === true && (
                    <div style={{ marginTop: '10px' }}>
                      <Field label="Data do Batismo" icon={Calendar}>
                        <input type="date" style={inputStyle(false)} value={extraInfo.baptismDate} max={getTodayLocalISO()} onChange={e => setExtraInfo(p => ({ ...p, baptismDate: e.target.value }))} required />
                      </Field>
                    </div>
                  )}
                </div>

                {/* Encontro com Deus */}
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: '0 0 10px' }}>Já foi para o Encontro com Deus?</p>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {[true, false].map(val => (
                      <label key={String(val)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', border: `1.5px solid ${encontroComDeus === val ? '#3b82f6' : '#e2e8f0'}`, background: encontroComDeus === val ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                        <input type="radio" name="encontroComDeus" checked={encontroComDeus === val} onChange={() => { setEncontroComDeus(val); if (!val) setExtraInfo(p => ({ ...p, encontroDate: '' })); }} style={{ accentColor: '#3b82f6' }} />
                        {val ? 'Sim' : 'Não'}
                      </label>
                    ))}
                  </div>
                  {encontroComDeus === true && (
                    <div style={{ marginTop: '10px' }}>
                      <Field label="Data do Encontro" icon={Calendar}>
                        <input type="date" style={inputStyle(false)} value={extraInfo.encontroDate} max={getTodayLocalISO()} onChange={e => setExtraInfo(p => ({ ...p, encontroDate: e.target.value }))} required />
                      </Field>
                    </div>
                  )}
                </div>

                {/* Observações */}
                <Field label="Registro de Encontros">
                  <textarea
                    style={{ ...inputStyle(false), resize: 'vertical', minHeight: '80px' }}
                    value={extraInfo.encounters}
                    onChange={e => setExtraInfo(p => ({ ...p, encounters: e.target.value.slice(0, 500) }))}
                    maxLength={500}
                    placeholder="Descreva se já participou de algum encontro..."
                  />
                  <p style={{ fontSize: '11px', color: '#94a3b8', margin: '2px 0 0', textAlign: 'right' }}>{extraInfo.encounters.length}/500</p>
                </Field>
              </div>

              <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button type="button" onClick={() => setShowCloseConfirm(true)} disabled={extraSaving} style={{ padding: '9px 18px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#fff', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>Cancelar</button>
                <button type="submit" disabled={extraSaving} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: extraSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Save size={14} /> {extraSaving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ── ABA CARTEIRINHA ──────────────────────────────────────────────── */}
      {activeTab === 'Carteirinha' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>

          {/* Card */}
          <div style={{ width: '300px', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', background: '#fff' }}>
            {/* Header */}
            <div style={{ height: '72px', background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 60%, #60a5fa 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              <div style={{ position: 'absolute', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', top: '-40px', right: '-20px' }} />
              <CreditCard size={16} color="rgba(255,255,255,0.7)" style={{ marginBottom: '2px' }} />
              <span style={{ color: '#fff', fontSize: '13px', fontWeight: '700', letterSpacing: '0.5px' }}>Igreja Apascentar</span>
            </div>

            {/* Avatar centralizado */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '-30px', position: 'relative', zIndex: 1 }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', border: '3px solid #fff', overflow: 'hidden', background: avatarSrc ? 'transparent' : roleConfig.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.18)' }}>
                {avatarSrc
                  ? <img src={avatarSrc} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: '22px', fontWeight: '700', color: '#fff' }}>{initials}</span>
                }
              </div>
            </div>

            {/* Dados + QR */}
            <div style={{ padding: '10px 20px 20px', textAlign: 'center' }}>
              <h3 style={{ margin: '8px 0 4px', fontSize: '17px', fontWeight: '700', color: '#1e293b' }}>{user.name}</h3>
              <span style={{ display: 'inline-block', fontSize: '11px', fontWeight: '600', color: '#fff', background: roleConfig.bg, borderRadius: '20px', padding: '2px 12px', marginBottom: '16px' }}>
                {roleConfig.label}
              </span>

              {/* QR Code */}
              <div ref={qrCanvasRef} style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
                <div style={{ padding: '10px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <QRCodeCanvas
                    value={String(user._id)}
                    size={110}
                    fgColor="#1e293b"
                    bgColor="#f8fafc"
                    level="M"
                  />
                </div>
              </div>

              {/* ID curto */}
              <p style={{ fontSize: '10px', color: '#94a3b8', fontFamily: 'monospace', letterSpacing: '2px', margin: '0 0 4px' }}>
                {String(user._id).slice(-10).toUpperCase()}
              </p>
              {user.createdAt && (
                <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>
                  Membro desde {format(new Date(user.createdAt), 'MMMM yyyy', { locale: ptBR })}
                </p>
              )}
            </div>
          </div>

          {/* Botão download */}
          <button
            onClick={handleDownloadQR}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #e2e8f0', background: '#fff', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
          >
            <Download size={15} /> Baixar QR Code
          </button>
        </div>
      )}

      {/* ── ABA SEGURANÇA ────────────────────────────────────────────────── */}
      {activeTab === 'Segurança' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Alterar senha */}
          <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Alterar Senha</h3>
            </div>
            <form onSubmit={handlePasswordChange}>
              <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Senha Atual" icon={Key}>
                    <div style={{ position: 'relative' }}>
                      <input type={showCurrentPwd ? 'text' : 'password'} style={{ ...inputStyle(false), paddingRight: '40px' }} value={passwordData.currentPassword} onChange={e => setPasswordData(p => ({ ...p, currentPassword: e.target.value }))} required placeholder="Sua senha atual" />
                      <button type="button" onClick={() => setShowCurrentPwd(s => !s)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                        {showCurrentPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </Field>
                </div>
                <Field label="Nova Senha" icon={Key}>
                  <div style={{ position: 'relative' }}>
                    <input type={showNewPwd ? 'text' : 'password'} style={{ ...inputStyle(false), paddingRight: '40px' }} value={passwordData.newPassword} onChange={e => setPasswordData(p => ({ ...p, newPassword: e.target.value }))} required minLength={6} placeholder="Mínimo 6 caracteres" />
                    <button type="button" onClick={() => setShowNewPwd(s => !s)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                      {showNewPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </Field>
                <Field label="Confirmar Nova Senha" icon={Key}>
                  <input type="password" style={inputStyle(false)} value={passwordData.confirmPassword} onChange={e => setPasswordData(p => ({ ...p, confirmPassword: e.target.value }))} required placeholder="Repita a nova senha" />
                  {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                    <p style={{ fontSize: '11px', color: '#ef4444', margin: '2px 0 0' }}>Senhas não coincidem</p>
                  )}
                </Field>
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" disabled={loading} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Key size={14} /> {loading ? 'Alterando...' : 'Alterar Senha'}
                </button>
              </div>
            </form>
          </div>

          {/* Config admin */}
          {canManageSettings && (
            <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Settings size={18} color="#3b82f6" />
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Configurações Administrativas</h3>
              </div>
              <div style={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: '#f8fafc', borderRadius: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Target size={20} color="#3b82f6" />
                    <div>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>Missões</p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{settings.missionsVisible ? 'Visíveis para membros' : 'Ocultas para membros'}</p>
                    </div>
                  </div>
                  <button onClick={async () => { setLoading(true); await toggleMissionsVisibility(); setLoading(false); }} disabled={loading}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer', background: settings.missionsVisible ? '#f0fdf4' : '#f1f5f9', color: settings.missionsVisible ? '#16a34a' : '#64748b' }}>
                    {settings.missionsVisible ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                    {settings.missionsVisible ? 'Habilitado' : 'Desabilitado'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>

    <ConfirmModal
      isOpen={showRemoveAvatar}
      onClose={() => setShowRemoveAvatar(false)}
      onConfirm={handleAvatarRemove}
      title="Remover foto"
      message="Tem certeza que deseja remover sua foto de perfil?"
      confirmText="Remover"
      cancelText="Cancelar"
      variant="danger"
    />
    <ConfirmModal
      isOpen={showCloseConfirm}
      onClose={() => setShowCloseConfirm(false)}
      onConfirm={() => { setExtraEditing(false); setShowCloseConfirm(false); syncFromUser(user); }}
      title="Descartar alterações"
      message="As alterações não salvas serão perdidas. Deseja continuar?"
      confirmText="Sim, descartar"
      cancelText="Cancelar"
      variant="danger"
    />
    </>
  );
};

export default Profile;
